import React, { useEffect, useCallback, useState, useRef } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { withRouter } from "react-router";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import LoadingIcon from "mdi-react/LoadingIcon";
import { Row, Col, Nav, NavLink, NavItem } from "reactstrap";

import {
  eventsConstants,
  sharedConstants,
  globalSystemConstants,
} from "../../constants";
import {
  customToast,
  convertBytesToMbs,
  calculateTotalFileSizeInMbs,
} from "../../shared/utility";
import Spinner from "../../shared/components/Spinner/Spinner";
import ViewGallery from "../../shared/components/ViewGallery/ViewGallery";
import { eventsActions } from "../../redux/actions";

const {
  ADD_TO_GALLERY,
  GET_EVENT_GALLERY,
  DELETE_FROM_GALLERY,
  FAVORITE_GALLERY_CLIP,
} = eventsConstants;

const TABS = {
  ACTIVE_GALLERY: "ACTIVE_GALLERY",
  DELETED_GALLERY: "DELETED_GALLERY",
};

const ViewEventDetail = (props) => {
  const {
    currentEvent,
    favoriteClip,
    getEventGallery,
    getGroupEventGallery,
    isAccessGroupGallery,
    isGroupReelExist,
  } = props;

  const { t } = useTranslation("common");

  const fileInputRef = useRef(null);
  const galleryUrlRef = useRef(null);
  const currentFilePrevUploadedSizeRef = useRef(0);

  const [files, setFiles] = useState([]);
  const [filesAdded, setFilesAdded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [downloadedFiles, setDownloadedFiles] = useState(0);
  const [currentFileName, setCurrentFileName] = useState("");
  const [currentFileSize, setCurrentFileSize] = useState("");
  const [isFileTypeReel, setIsFileTypeReel] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [activeTab, setActiveTab] = useState(TABS.ACTIVE_GALLERY);
  const [totalDownloadedFiles, setTotalDownloadedFiles] = useState(0);
  const [counterFilesUploaded, setCounterFilesUploaded] = useState(1);
  const [totalUploadedFileSize, setTotalUploadedFileSize] = useState(0);
  const [totalFileSizeToUpload, setTotalFileSizeToUpload] = useState(0);
  const [currentFileUploadedSize, setCurrentFileUploadedSize] = useState(0);
  const [counterTotalFilesToUpload, setCounterTotalFilesToUpload] = useState(0);

  const sharedGalleryUrl = isAccessGroupGallery
    ? `${window.config.sharedGroupGalleryUrl}${currentEvent}`
    : `${window.config.sharedGalleryUrl}${currentEvent}`;

  let isActiveGallery = activeTab === TABS.ACTIVE_GALLERY;

  const toggle = (tab, isActive) => {
    if (isAccessGroupGallery) {
      getGroupEventGallery(currentEvent, isActive);
    } else {
      getEventGallery(currentEvent, isActive);
    }

    if (activeTab !== tab) setActiveTab(tab);
  };

  const spinner = useCallback(
    (type) => props.spinnerActions.findIndex((f) => f === type),
    [props.spinnerActions]
  );

  useEffect(() => {
    if (isAccessGroupGallery) {
      getGroupEventGallery(currentEvent, true);
    } else {
      getEventGallery(currentEvent, true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setFiles(
      Array.isArray(props.eventGalleryData) ? props.eventGalleryData : []
    );
    // eslint-disable-next-line
  }, [props.eventGalleryData]);

  const handleDelete = async () => {
    const filesToDeleteFromServer = selectedFiles.filter((file) => file.id);
    const filesToRemoveLocally = selectedFiles.filter((file) => !file.id);

    if (filesToDeleteFromServer.length > 0) {
      const idArray = filesToDeleteFromServer.map((file) => file.id);

      props.deleteFromGallery(
        { ids: idArray },
        setSelectedFiles,
        isActiveGallery,
        refreshActiveGallery
      );
    }

    if (filesToRemoveLocally.length > 0) {
      setFiles((prevFiles) =>
        prevFiles.filter((file) => !filesToRemoveLocally.includes(file))
      );
    }

    if (
      filesToRemoveLocally.length > 0 &&
      filesToDeleteFromServer.length === 0
    ) {
      setSelectedFiles([]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*, video/*, .heic, .heif",
    onDrop: (acceptedFiles) => {
      setFiles((prevFiles) =>
        prevFiles.concat(
          acceptedFiles.map((file) => ({
            file,
            url: URL.createObjectURL(file),
            selected: false,
          }))
        )
      );
    },
  });

  const handleButtonClick = (value) => {
    fileInputRef.current.click();
    setIsFileTypeReel(value);
  };

  const handleFileInputChange = async (event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles.length > 0) {
      props.activateOverlay();
    }
    // size limit: total payload size limit
    let totalSelectedFilesArr = Array.from(selectedFiles);
    let totalPayloadSize = calculateTotalFileSizeInMbs(totalSelectedFilesArr);
    if (totalPayloadSize > process.env.REACT_APP_GALLERY_UPLOAD_PAYLOAD_LIMIT) {
      customToast(
        `Upto ${process.env.REACT_APP_GALLERY_UPLOAD_PAYLOAD_LIMIT} MBs are allowed in an attempt`,
        toast.TYPE.ERROR
      );
      props.diActivateOverlay();
      return null;
    }
    // size limit: each file size limit
    let fileWithInvalidSizeArr = totalSelectedFilesArr.filter((file) => {
      return (
        file.size / sharedConstants.KB_TO_MB_BYTES >=
        process.env.REACT_APP_GALLERY_UPLOAD_FILE_SIZE_LIMIT
      );
    });
    if (fileWithInvalidSizeArr.length > 0) {
      customToast(
        `Each File size should be under ${process.env.REACT_APP_GALLERY_UPLOAD_FILE_SIZE_LIMIT}MB.`,
        toast.TYPE.ERROR
      );
      props.diActivateOverlay();
      return null;
    }
    const convertedFiles = Array.from(selectedFiles).map((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (
        !file.type.includes("image") &&
        !file.type.includes("video") &&
        fileExtension !== "heic" &&
        fileExtension !== "heif"
      ) {
        customToast("File format not supported", toast.TYPE.ERROR);
        props.diActivateOverlay();
        return null;
      } else if (isFileTypeReel && !file.type.includes("video")) {
        customToast("Only Videos Supported In Reels", toast.TYPE.ERROR);
        props.diActivateOverlay();
        return null;
      }

      return {
        file,
        url: sharedConstants.IS_GALLERY_UPLOAD_PREVIEW,
        selected: false,
      };
    });

    // Filter out any null entries from unsupported formats
    const validFiles = convertedFiles.filter((file) => file !== null);

    setFiles((prevFiles) => {
      setFilesAdded(true); // Set the flag to true when files are added
      return [...(Array.isArray(prevFiles) ? prevFiles : []), ...validFiles];
    });
  };

  const handleAddToGallery = () => {
    let formdata = new FormData();
    const filesToSend = files.filter((file) => !file.id);
    filesToSend.forEach((file) => {
      formdata.append("file", file.file);
    });

    if (isAccessGroupGallery) {
      formdata.append("eventGroupId", currentEvent);
    } else {
      formdata.append("eventId", currentEvent);
    }

    formdata.append("SbUserId", 0);
    formdata.append("UserId", props.userId);
    if (isFileTypeReel) {
      formdata.append("contentType", "reel");
    } else {
      formdata.append("contentType", "file");
    }
    if (filesToSend.length > 0) {
      const options = (progressEvent) => {
        const { loaded, total } = progressEvent;
        let percent = Math.floor((loaded * 100) / total);
        if (percent <= 100) {
          setCurrentFileUploadedSize(loaded);
        }
      };

      props.addToGallery(
        formdata,
        options,
        null,
        counterFilesUploaded,
        setCounterFilesUploaded,
        totalUploadedFileSize,
        setTotalFileSizeToUpload,
        currentFilePrevUploadedSizeRef,
        setCurrentFileName,
        setCurrentFileSize,
        refreshActiveGallery
      );
      setIsFileTypeReel(false);
      setCounterFilesUploaded(1);
    }
    setSelectedFiles([]);
  };

  useEffect(() => {
    setTotalUploadedFileSize(
      totalUploadedFileSize +
        convertBytesToMbs(currentFileUploadedSize) -
        convertBytesToMbs(currentFilePrevUploadedSizeRef.current)
    );
    // keeping percentage in float to pass this condition progress > 0 and to show it as soon as uploading starts
    let updatedPercentage = (
      ((totalUploadedFileSize +
        convertBytesToMbs(currentFileUploadedSize) -
        convertBytesToMbs(currentFilePrevUploadedSizeRef.current)) /
        totalFileSizeToUpload) *
      100
    ).toFixed(2);
    updatedPercentage <= 100 && updatedPercentage > 0
      ? setUploadPercentage(updatedPercentage)
      : setUploadPercentage(100);

    currentFilePrevUploadedSizeRef.current = currentFileUploadedSize;
    // eslint-disable-next-line
  }, [currentFileUploadedSize]);

  useEffect(() => {
    if (filesAdded) {
      setFilesAdded(false);
      // Call handleAddToGallery only if files have been added
      handleAddToGallery();
    }
    // eslint-disable-next-line
  }, [files]);

  useEffect(() => {
    setUploadPercentage(0);
    setTotalFileSizeToUpload(0);
    setTotalUploadedFileSize(0);
    currentFilePrevUploadedSizeRef.current = 0;
  }, [props.eventGalleryData]);

  function copyToClipboard(e) {
    galleryUrlRef.current.select();
    document.execCommand("copy");
    e.target.focus();
    alert("Link Copied");
  }

  const favoriteClipHandle = (clipId) => {
    favoriteClip(clipId, refreshActiveGallery);
  };

  const refreshActiveGallery = () => {
    if (isAccessGroupGallery) {
      getGroupEventGallery(currentEvent, isActiveGallery);
    } else {
      getEventGallery(currentEvent, isActiveGallery);
    }
  };

  return (
    <>
      {spinner(GET_EVENT_GALLERY) &&
      spinner(DELETE_FROM_GALLERY) &&
      spinner(FAVORITE_GALLERY_CLIP) ? (
        <>
          <div className="tab-pane fade show active">
            {isAccessGroupGallery ? (
              isGroupReelExist ? (
                <div className="row d-flex justify-content-center">
                  <div className="col-md-5 w-100 d-flex justify-content-center">
                    <div {...getRootProps()} className="dropzone-content">
                      <input
                        {...getInputProps()}
                        ref={fileInputRef}
                        disabled={uploadPercentage > 0}
                        onChange={handleFileInputChange}
                      />
                      <button
                        className="gallery-btn mr-md-4 mb-2 mw-175"
                        disabled={uploadPercentage > 0}
                        onClick={() => handleButtonClick(true)}
                      >
                        Upload Combined Reels
                      </button>
                    </div>
                  </div>
                </div>
              ) : null
            ) : (
              <div className="row d-flex justify-content-center">
                <div className="col-md-5 w-100 d-flex justify-content-center">
                  <div {...getRootProps()} className="dropzone-content">
                    <input
                      {...getInputProps()}
                      ref={fileInputRef}
                      disabled={uploadPercentage > 0}
                      onChange={handleFileInputChange}
                    />
                    <button
                      className="gallery-btn mr-md-4 mb-2 mw-175"
                      disabled={uploadPercentage > 0}
                      onClick={() => handleButtonClick(false)}
                    >
                      Upload files
                    </button>
                    <button
                      className="gallery-btn mr-md-4 mb-2 mw-175"
                      disabled={uploadPercentage > 0}
                      onClick={() => handleButtonClick(true)}
                    >
                      Upload Reels
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="row d-flex justify-content-center">
              <div className="col-md-5 w-100 d-flex justify-content-center">
                <input
                  type="text"
                  readonly="true"
                  name="galleryLink"
                  className="w-100 p-2 my-2"
                  ref={galleryUrlRef}
                  value={sharedGalleryUrl}
                />
              </div>
              <div className="col-md-1 w-100 d-flex justify-content-center">
                <button
                  className="gallery-btn my-2 mr-md-4 h-75 mb-3 mw-175"
                  onClick={copyToClipboard}
                >
                  COPY
                </button>
              </div>
            </div>
            <Row className="my-3">
              <Col>
                <Nav tabs style={{ height: "31px" }}>
                  <NavItem>
                    <NavLink
                      className={`${
                        activeTab === TABS.ACTIVE_GALLERY ? "active" : ""
                      }`}
                      onClick={() => {
                        toggle(TABS.ACTIVE_GALLERY, true);
                      }}
                      style={{ padding: "13px" }}
                    >
                      Active Gallery
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={` ${
                        activeTab === TABS.DELETED_GALLERY ? "active" : ""
                      }`}
                      onClick={() => {
                        toggle(TABS.DELETED_GALLERY, false);
                      }}
                      style={{ padding: "13px" }}
                    >
                      Deleted Items
                    </NavLink>
                  </NavItem>
                </Nav>
              </Col>
            </Row>
            <div className="">
              {files?.length > 0 ? (
                <ViewGallery
                  files={files}
                  isTabsDisplay
                  isModalAllowed
                  isShareAllowed
                  isAllowDownload
                  isAsyncUploadCall
                  setFiles={setFiles}
                  activeFlg={isActiveGallery}
                  handleDelete={handleDelete}
                  progress={uploadPercentage}
                  selectedFiles={selectedFiles}
                  downloadedFiles={downloadedFiles}
                  currentFileName={currentFileName}
                  currentFileSize={currentFileSize}
                  setSelectedFiles={setSelectedFiles}
                  setDownloadedFiles={setDownloadedFiles}
                  setCurrentFileName={setCurrentFileName}
                  setCurrentFileSize={setCurrentFileSize}
                  favoriteClipHandle={favoriteClipHandle}
                  counterFilesUploaded={counterFilesUploaded}
                  setCounterFilesUploaded={setCounterFilesUploaded}
                  setTotalDownloadedFiles={setTotalDownloadedFiles}
                  downloadSelectedFiles={props.downloadSelectedFiles}
                  counterTotalFilesToUpload={counterTotalFilesToUpload}
                  setCounterTotalFilesToUpload={setCounterTotalFilesToUpload}
                />
              ) : (
                <div className="d-flex justify-content-center align-items-center">
                  <p>
                    {activeTab === TABS.ACTIVE_GALLERY
                      ? t("no_images")
                      : t("no_deleted_files")}
                  </p>
                </div>
              )}
            </div>
            <div className="col-md-6 justify-content-start d-flex mt-4 px-0">
              <button
                type="btn"
                className="btn btn-info"
                onClick={() => props.onClose()}
              >
                Back
              </button>
            </div>
          </div>
          <Spinner
            downloadedFiles={downloadedFiles}
            totalFiles={totalDownloadedFiles}
          />
        </>
      ) : (
        <div className="text-center mt-4">
          <LoadingIcon className="loading-spinner" />
        </div>
      )}
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  userId: state.authentication.userId,
  spinnerActions: state.system.spinnerActions,
  eventGalleryData: state.events.eventGalleryData,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getEventGallery: (id, isActive) =>
    dispatch(eventsActions.getEventGallery(id, isActive)),
  getGroupEventGallery: (id, isActive) =>
    dispatch(eventsActions.getGroupEventGallery(id, isActive)),
  favoriteClip: (clipId, getUpdatedData) =>
    dispatch(eventsActions.favoriteClip(clipId, getUpdatedData)),
  downloadSelectedFiles: (eventIdsArr, setDownloadedFiles) =>
    dispatch(
      eventsActions.downloadSelectedFiles(eventIdsArr, setDownloadedFiles)
    ),
  deleteFromGallery: (dataObj, setSelectedFiles, isActive, onSuccess) =>
    dispatch(
      eventsActions.deleteFromGallery(
        dataObj,
        setSelectedFiles,
        isActive,
        onSuccess
      )
    ),
  addToGallery: (
    data,
    options,
    dummy,
    counterFilesUploaded,
    setCounterFilesUploaded,
    totalUploadedFileSize,
    setTotalFileSizeToUpload,
    currentFilePrevUploadedSizeRef,
    setCurrentFileName,
    setCurrentFileSize,
    onSuccess
  ) =>
    dispatch(
      eventsActions.addToGallery(
        data,
        options,
        dummy,
        counterFilesUploaded,
        setCounterFilesUploaded,
        totalUploadedFileSize,
        setTotalFileSizeToUpload,
        currentFilePrevUploadedSizeRef,
        setCurrentFileName,
        setCurrentFileSize,
        onSuccess
      )
    ),
  activateOverlay: () =>
    dispatch({
      type: globalSystemConstants.OVERLAY_ACTIVATE,
      data: ADD_TO_GALLERY,
    }),
  diActivateOverlay: () =>
    dispatch({
      type: globalSystemConstants.OVERLAY_DIACTIVATE,
      data: ADD_TO_GALLERY,
    }),
});

// Mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ViewEventDetail));
